import { get, uniq, compact } from 'lodash';
import { MS_PHASE_3_ENDPOINTS, schoolService } from 'utils/msEndpointConstants';
import { retrieveObjectMapper } from 'utils';
import {
  transformCurrentSystemAnnouncementByTarget,
  transformAllSystemAnnouncementTargets,
  transformAllSystemAnnouncements,
} from '../utils/ms-utils/ms-phase-3/SchoolUtils';
import DatadogHandler from '../utils/datadog';
import {
  DEFAULT_ERROR,
  DEFAULT_PAGE,
  DEFAULT_ITEMS_PER_PAGE,
  MAX_PER_PAGE,
} from '../utils/constants';
import { convertObjToCamelCase, convertObjToSnakeCase } from '../utils';

const fileName = 'src/services/SystemAnnouncementService.js';
const { SCHOOL } = MS_PHASE_3_ENDPOINTS;

export const msGetCurrentSystemAnnouncement = async (_, reqBody) => {
  try {
    const resp = await schoolService().get(
      SCHOOL.getCurrentSystemAnnouncement(get(reqBody, 'target'))
    );
    return {
      success: true,
      data: {
        getCurrentSystemAnnouncementV2: retrieveObjectMapper(
          get(resp, 'data', {}),
          ['ID:id', 'title', 'message', 'from', 'to']
        ),
      },
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msGetCurrentSystemAnnouncement',
        fileName,
      },
      'error'
    );
    return {
      data: undefined,
      success: false,
      error: get(error, 'message', DEFAULT_ERROR),
    };
  }
};

export const msGetAllSystemAnnouncements = async (_, reqBody) => {
  try {
    const pagination = get(reqBody, 'pagination', {});
    const paginationParams = {
      page: get(pagination, 'page', DEFAULT_PAGE),
      perPage: get(pagination, 'perPage', DEFAULT_ITEMS_PER_PAGE),
    };

    const announcementsResp = await schoolService().get(
      SCHOOL.getAllSystemAnnouncements(paginationParams)
    );

    const announcementsData = get(announcementsResp, 'data.data', []) || [];
    const totalCount = get(announcementsResp, 'data.totalCount', 0);

    if (!announcementsData || announcementsData.length === 0) {
      return {
        success: true,
        data: {
          getAllSystemAnnouncementsV2: {
            data: [],
            totalCount: 0,
          },
        },
      };
    }

    const announcementIds = compact(
      announcementsData.map(announcement => announcement.id)
    );

    const targetMappingsResp = await schoolService().get(
      SCHOOL.getSystemAnnouncementTargetMappings(announcementIds, {
        page: DEFAULT_PAGE,
        perPage: MAX_PER_PAGE,
      })
    );

    const targetMappingsData = get(targetMappingsResp, 'data.data', []) || [];

    const targetIds = uniq(
      compact(targetMappingsData.map(mapping => mapping.fk_target))
    );

    let targetsData = [];
    if (targetIds.length > 0) {
      const targetsResp = await schoolService().get(
        SCHOOL.getSystemAnnouncementTargets(targetIds)
      );
      targetsData = get(targetsResp, 'data.data', []) || [];
    }

    const transformedData = transformAllSystemAnnouncements(
      announcementsData,
      targetMappingsData,
      targetsData,
      totalCount
    );

    return {
      success: true,
      data: {
        getAllSystemAnnouncementsV2: transformedData,
      },
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msGetAllSystemAnnouncements',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(error, 'message', DEFAULT_ERROR),
    };
  }
};

export const msGetAllSystemAnnouncementTargets = async () => {
  try {
    const targetsResp = await schoolService().get(
      SCHOOL.getAllSystemAnnouncementTargets
    );

    const targetsData = get(targetsResp, 'data.data', []) || [];
    const transformedData = transformAllSystemAnnouncementTargets(targetsData);

    return {
      success: true,
      data: {
        getAllSystemAnnouncementTargets: transformedData,
      },
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msGetAllSystemAnnouncementTargets',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(error, 'message', DEFAULT_ERROR),
    };
  }
};

export const msGetCurrentSystemAnnouncementByTarget = async (_, reqBody) => {
  try {
    const announcementResp = await schoolService().get(
      SCHOOL.getCurrentSystemAnnouncement(get(reqBody, 'target'))
    );

    const announcementData = get(announcementResp, 'data', {});

    if (!announcementData || !announcementData.id) {
      return {
        success: true,
        data: {
          getCurrentSystemAnnouncementV2: null,
        },
      };
    }

    const targetMappingsResp = await schoolService().get(
      SCHOOL.getSystemAnnouncementTargetMappings([announcementData?.id])
    );

    const targetMappingsData = get(targetMappingsResp, 'data.data', []) || [];

    const targetIds = uniq(
      compact(targetMappingsData.map(mapping => mapping.fk_target))
    );

    let targetsData = [];
    if (targetIds.length > 0) {
      const targetsResp = await schoolService().get(
        SCHOOL.getSystemAnnouncementTargets(targetIds)
      );
      targetsData = get(targetsResp, 'data.data', []) || [];
    }

    const transformedData = transformCurrentSystemAnnouncementByTarget(
      announcementData,
      targetMappingsData,
      targetsData
    );

    return {
      success: true,
      data: transformedData,
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msGetCurrentSystemAnnouncementByTarget',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(error, 'message', DEFAULT_ERROR),
    };
  }
};

export const msUpdateSystemAnnouncement = async (_, reqBody) => {
  try {
    const resp = await schoolService().put(
      SCHOOL.updateSystemAnnouncement(get(reqBody, 'ID')),
      {
        announcement: {
          fk_school: get(reqBody, 'announcement.fkSchool'),
          from: get(reqBody, 'announcement.from'),
          message: get(reqBody, 'announcement.message'),
          title: get(reqBody, 'announcement.title'),
          to: get(reqBody, 'announcement.to'),
        },
        target_ids: get(reqBody, 'targetIDs') || [],
      }
    );
    return {
      success: true,
      data: {
        updateSystemAnnouncementV2: retrieveObjectMapper(
          get(resp, 'data', {}),
          ['ID:id']
        ),
      },
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msUpdateSystemAnnouncement',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: [
        {
          message: get(
            error,
            'response.data.error',
            get(error, 'message', DEFAULT_ERROR)
          ),
        },
      ],
    };
  }
};

export const msAddNewSystemAnnouncement = async (_, reqBody) => {
  try {
    const body = {
      announcement: convertObjToSnakeCase(reqBody?.announcement),
      target_ids: reqBody?.targetIDs || [],
    };

    const schoolSvc = schoolService();
    const resp = await schoolSvc.post(
      ...SCHOOL.createSystemAnnouncements(body)
    );

    return {
      data: {
        addSystemAnnouncementV2:
          resp?.data && convertObjToCamelCase(resp?.data),
      },
      success: true,
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msAddNewSystemAnnouncement',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: [
        {
          message:
            get(error, 'response.data.error') ||
            get(error, 'message', DEFAULT_ERROR),
        },
      ],
    };
  }
};

export const msDeleteSystemAnnouncement = async (_, reqBody) => {
  try {
    await schoolService().delete(
      SCHOOL.deleteSystemAnnouncement(get(reqBody, 'ID'))
    );
    return {
      success: true,
      data: {
        deleteSystemAnnouncement: true,
      },
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msDeleteSystemAnnouncement',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(error, 'message', DEFAULT_ERROR),
    };
  }
};

export const msEndSystemAnnouncement = async () => {
  try {
    const res = await Promise.resolve();
    return {
      success: true,
      data: {
        endSystemAnnouncement: get(res, 'data.success', false),
      },
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msEndSystemAnnouncement',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(error, 'message', DEFAULT_ERROR),
    };
  }
};

export default { msGetCurrentSystemAnnouncement };
